import { useCallback, useEffect, useState } from 'react';
import { Card, Form } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch } from '../../../redux/HooksRedux';
import {
  LogDisplayItem,
  LogEntry,
  Meta,
  OptionI,
  paramsI,
  RoleFormInterface,
  roleFormModuleI
} from '../../../../types/interface';
import * as roleAction from '../../../redux/role/roleAction';
import {
  errorNotification,
  successNotification
} from 'base/notifications/Notifications';
import {
  capitalizeFirstLetter,
  checkAccess,
  convertToLabelValue,
  logEntriesToDisplayItems,
  parseIntoNumber
} from 'utils/helpers';
import { ROLE_MATRIX } from 'constant/RoutesUrls';
import { RoleFormValidationSet } from 'utils/validations/ValidationsSet/RoleFormValidationSet';
import validationFunctions from 'utils/validationUtils';
import { t } from 'i18next';
import ConditionalRender from 'base/ConditionalRender/ConditionalRender';
import { StandardButton } from 'base/StandardButton/StandardButton';
import StandardInput from 'base/StandardInput/StandardInput';
import StandardCheckbox from 'base/StandardCheckbox/StandardCheckbox';
import { StandardLoader } from 'base/Loader/Loader';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import DeleteModel from 'base/DeleteModal/DeleteModal';
import StandardTab from 'base/StandardTab/StandardTab';
import { tabData } from 'utils/staticData';
import {
  GERMAN,
  logPerPage,
  LogType,
  modules,
  SELECTED_LANGUAGE
} from 'utils/enums/misc';
import { getCookie } from 'utils/cookieUtils';
import InfinityScroll from 'base/InfonityScroll/InfinityScroll';
import LogTimeline from 'components/timelines/LogTimeline';
import { AxiosResponse } from 'axios';
import * as logAction from '../../../redux/logs/logAction';

export const RoleForm = () => {
  const [formData, setFormData] = useState<OptionI[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [moduleArray, setModuleArray] = useState<roleFormModuleI[]>([]);
  const [permissionMsg, setPermissionMsg] = useState('');
  const [showError, setShowError] = useState(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { editRoleId } = useParams();
  const [roleNameData, setRoleNameData] = useState<RoleFormInterface>({
    role_name: '',
    role_name_de: ''
  });

  // Logs states
  const [logCurrentPage, setLogCurrentPage] = useState(1);
  const [logs, setLogs] = useState<LogDisplayItem[]>([]);
  const [activeTab, setActiveTab] = useState<LogType | null>(LogType.FORM);
  const [hasMoreLogs, setHasMoreLogs] = useState(false);
  const [logLoading, setLogLoading] = useState(false);

  const userPermission = useSelector(
    (state: RootState) => state.userProfile.permission
  );
  const [deleteRoleData, setDeleteRoleData] = useState<{
    role_id: number;
    showDeletePopup: boolean;
  }>({
    role_id: 0,
    showDeletePopup: false
  });

  const permissionOptions = [
    { label: 'view', value: 'view' },
    { label: 'create', value: 'create' },
    { label: 'edit', value: 'edit' },
    { label: 'delete', value: 'delete' }
  ];

  const getAllModule = async () => {
    setIsLoading(true);
    try {
      const response = await dispatch(roleAction.getAllModule());
      if (response?.status === 200) {
        const convertValue = convertToLabelValue(
          response.data.modules,
          'module_name',
          'module_id'
        );
        setFormData(convertValue);
        setIsLoading(false);
      } else {
        errorNotification(response?.data?.message);
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
      console.log(error);
    }
  };
  const getSingleRole = async () => {
    if (!checkAccess([modules.ROLE.edit], userPermission)) {
      navigate(ROLE_MATRIX);
      return;
    }
    setIsLoading(true);
    try {
      const response = await dispatch(
        roleAction.getSingleRole(parseInt(editRoleId as string))
      );
      if (response?.status === 200) {
        const { role_name, modules, role_name_en, role_name_de } =
          response.data.data;
        setRoleNameData({
          role_name,
          role_name_de
        });
        setModuleArray(modules);
        setIsLoading(false);
      } else {
        errorNotification(response.data?.message);
        setIsLoading(false);
        navigate(ROLE_MATRIX);
      }
    } catch (error) {
      setIsLoading(false);
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (editRoleId) {
      getSingleRole();
    }
  }, [editRoleId]);

  useEffect(() => {
    getAllModule();
  }, []);

  const handleModuleChange = (checked: boolean, module: string) => {
    const updatedArray = [...moduleArray];
    if (checked) {
      updatedArray.push({
        module,
        permissions: ['create', 'view', 'edit', 'delete']
      });
    } else {
      const index = updatedArray.findIndex(item => item.module === module);
      if (index !== -1) updatedArray.splice(index, 1);
    }
    setModuleArray(updatedArray);
  };

  const handlePermissionChange = (
    checked: boolean,
    permission: 'create' | 'view' | 'edit' | 'delete',
    module_id: string
  ) => {
    const updatedArray = [...moduleArray];
    const moduleIndex = updatedArray.findIndex(
      item => item.module === module_id
    );

    if (moduleIndex !== -1) {
      const module = updatedArray[moduleIndex];

      if (checked) {
        // Add the permission to the module
        module.permissions.push(permission);

        // If create, edit, or delete is checked, ensure 'view' is also checked
        if (['create', 'edit', 'delete'].includes(permission)) {
          if (!module.permissions.includes('view')) {
            module.permissions.push('view');
          }
        }
      } else {
        // Remove the permission from the module
        module.permissions = module.permissions.filter(
          (perm: string) => perm !== permission
        );

        // If 'view' is unchecked, uncheck 'create', 'edit', and 'delete' as well
        if (permission === 'view') {
          module.permissions = module.permissions.filter(
            (perm: string) => !['create', 'edit', 'delete'].includes(perm)
          );
        }

        // Check if create, edit, and delete are all unchecked, then keep view unchecked
        if (
          ['create', 'edit', 'delete'].every(
            perm =>
              !module.permissions.includes(
                perm as 'create' | 'view' | 'edit' | 'delete'
              )
          )
        ) {
          module.permissions = module.permissions.filter(
            (perm: string) => perm !== 'view'
          );
        }
      }

      // If no permissions left, remove the module entry
      if (module.permissions.length === 0) {
        updatedArray.splice(moduleIndex, 1);
      }
    } else if (checked) {
      // If the module is not found and a permission is checked, add it with the permission
      updatedArray.push({
        module: module_id,
        permissions: permission === 'view' ? [permission] : ['view', permission] // Automatically add "view" if "create", "edit", or "delete" is checked
      });
    }

    setModuleArray(updatedArray);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const apiData = {
      role_name: roleNameData.role_name,
      role_name_de: roleNameData.role_name_de,
      modules: moduleArray,
      is_archived: true
    };

    const validate = validationFunctions.checkFormValidity(
      apiData,
      RoleFormValidationSet
    );
    if (!validate) {
      setShowError(true);
      if (moduleArray.length < 1) {
        setPermissionMsg(t('validation.role.module_array'));
      } else {
        setPermissionMsg('');
      }
      setIsLoading(false);
      return;
    }
    setShowError(true);
    setIsLoading(true);
    if (editRoleId && checkAccess([modules.ROLE.edit], userPermission)) {
      try {
        const response = await dispatch(
          roleAction.updateRole(parseInt(editRoleId!), apiData)
        );
        if (response.status === 200) {
          successNotification(response.data?.message);
          navigate(ROLE_MATRIX);
          setIsLoading(false);
        } else {
          errorNotification(response.data?.message);
          setIsLoading(false);
        }
      } catch (error) {
        setIsLoading(false);
        console.log(error);
      }
    } else if (checkAccess([modules.ROLE.create], userPermission)) {
      try {
        const response = await dispatch(roleAction.createRole(apiData));
        if (response.status === 201) {
          successNotification(response.data.message);
          navigate(ROLE_MATRIX);
          setIsLoading(false);
        }
      } catch (error) {
        setIsLoading(false);
        console.log(error);
      }
    } else {
      setIsLoading(false);
      navigate(ROLE_MATRIX);
    }
  };

  const getRoleLog = useCallback(
    async (roleId: number) => {
      try {
        const params: paramsI = {
          page: 1,
          limit: logPerPage * logCurrentPage,
          keyword: ''
        };
        const response: AxiosResponse<{ logs: LogEntry[]; meta: Meta }> =
          await dispatch(logAction.getLogByRoleId(roleId, params));
        if (params.limit >= response.data.meta.totalRecords) {
          setHasMoreLogs(false);
        } else {
          setHasMoreLogs(true);
        }

        setLogs(
          logEntriesToDisplayItems(
            response.data.logs,
            getCookie(SELECTED_LANGUAGE) === GERMAN
          )
        );
      } catch (error) {
        console.error(error);
      } finally {
        setLogLoading(false);
      }
    },
    [logCurrentPage, logPerPage]
  );

  useEffect(() => {
    if (moduleArray?.length > 0) {
      setPermissionMsg('');
    } else if (moduleArray?.length == 0) {
      setPermissionMsg(t('validation.role.module_array'));
    }
  }, [moduleArray]);

  useEffect(() => {
    setPermissionMsg('');
  }, []);

  useEffect(() => {
    if (editRoleId && activeTab === LogType.LOGS) {
      getRoleLog(Number(editRoleId));
    }
  }, [editRoleId, logCurrentPage]);

  useEffect(() => {
    if (activeTab === LogType.LOGS && logs.length === 0) {
      getRoleLog(Number(editRoleId));
    }
  }, [activeTab]);

  const handleDeleteRole = async () => {
    setIsLoading(true);
    try {
      const response = await dispatch(
        roleAction.deleteRole(deleteRoleData.role_id)
      );
      if (response?.status === 200) {
        successNotification(response.data?.message);
        setDeleteRoleData({ role_id: 0, showDeletePopup: false });
        navigate(ROLE_MATRIX);
        setIsLoading(false);
      } else {
        errorNotification(response?.data?.message);
        setDeleteRoleData({ role_id: 0, showDeletePopup: false });
        setIsLoading(false);
      }
    } catch (error) {
      setDeleteRoleData({ role_id: 0, showDeletePopup: false });
      console.log(error);
      setIsLoading(false);
    }
  };

  const renderDeletePopup = () => {
    if (deleteRoleData.showDeletePopup) {
      return (
        <DeleteModel
          onCancel={() =>
            setDeleteRoleData({ role_id: 0, showDeletePopup: false })
          }
          onConfirm={handleDeleteRole}
        />
      );
    }
  };

  return (
    <Card>
      <ConditionalRender condition={!isLoading} fallBack={<StandardLoader />}>
        <Form onSubmit={handleSubmit}>
          <Card.Header className="py-2">
            <div className="row justify-content-between align-items-center">
              <div className="w-auto">
                <h3 className="mb-0">
                  {capitalizeFirstLetter(t('header.text.text_8'))}{' '}
                </h3>
              </div>
              <div className="d-flex gap-2 w-auto">
                <StandardButton
                  buttonText={t('general.common_text.text_15')}
                  onClick={() => navigate(ROLE_MATRIX)}
                  variant="outline-danger"
                />
                <StandardButton
                  buttonText={
                    editRoleId
                      ? capitalizeFirstLetter(t('general.common_text.text_18'))
                      : capitalizeFirstLetter(t('general.common_text.text_2'))
                  }
                  onClick={() => console.log('save')}
                  type="submit"
                />
                <ConditionalRender
                  condition={
                    checkAccess([modules.ROLE.delete], userPermission) &&
                    !!editRoleId
                  }
                >
                  <StandardButton
                    onClick={() => {
                      setDeleteRoleData({
                        role_id: parseIntoNumber(editRoleId),
                        showDeletePopup: true
                      });
                    }}
                    buttonText={capitalizeFirstLetter(
                      t('general.common_text.text_14')
                    )}
                  />
                </ConditionalRender>
              </div>
            </div>
          </Card.Header>
          <Card.Body>
            <ConditionalRender condition={!!editRoleId}>
              <div>
                <StandardTab
                  activeKey={activeTab}
                  data={tabData}
                  onChange={val => setActiveTab(val)}
                />
              </div>
            </ConditionalRender>
            <ConditionalRender
              condition={activeTab === LogType.FORM}
              fallBack={
                <InfinityScroll
                  childrenIdPrefix="role-log"
                  currentPage={logCurrentPage}
                  setCurrentPage={setLogCurrentPage}
                  dataLength={logs.length}
                  hasMore={hasMoreLogs}
                  limitPerPage={logPerPage}
                  loading={logLoading}
                  setLoading={setLogLoading}
                >
                  <LogTimeline data={logs} idPrefix="role-log" />
                </InfinityScroll>
              }
            >
              <div className="row">
                <div className="col-lg-4">
                  <StandardInput
                    type="text"
                    value={roleNameData.role_name}
                    placeholder={t('role.placeholder.text_1')}
                    onChange={e =>
                      setRoleNameData({
                        ...roleNameData,
                        role_name: e?.toString()?.trimStart()
                      })
                    }
                    showError={showError}
                    validations={RoleFormValidationSet.role_name}
                    className=""
                    id="role_name"
                    label={t('role.placeholder.text_1')}
                  />
                </div>
                <div className="col-lg-4">
                  <StandardInput
                    type="text"
                    value={roleNameData.role_name_de}
                    placeholder={t('role.placeholder.text_2')}
                    onChange={e =>
                      setRoleNameData({
                        ...roleNameData,
                        role_name_de: e?.toString()?.trimStart()
                      })
                    }
                    showError={showError}
                    validations={RoleFormValidationSet.role_name_de}
                    className=""
                    id="role_name"
                    label={t('role.placeholder.text_2')}
                  />
                </div>
              </div>
              <div className="row mb-1">
                <div className="col-lg-12 fs-12 text-danger font-normal">
                  {permissionMsg}
                </div>
              </div>
              <div className="row mb-1 fs-14 font-semi-bold">
                <div className="col-4">
                  {capitalizeFirstLetter(t('role.general.text_2'))}
                </div>
                <div className="col-2">
                  {capitalizeFirstLetter(t('general.common_text.text_1'))}
                </div>
                <div className="col-2">
                  {capitalizeFirstLetter(t('role.general.text_3'))}
                </div>
                <div className="col-2">
                  {capitalizeFirstLetter(t('general.common_text.text_13'))}
                </div>
                <div className="col-2">
                  {capitalizeFirstLetter(t('general.common_text.text_14'))}
                </div>
              </div>
              {formData.map((item, index) => {
                const foundModule = moduleArray.find(
                  moduleItem => moduleItem.module === item.value
                );

                return (
                  <div className="row" key={index}>
                    <div className="col-4">
                      <StandardCheckbox
                        label={item.label as string}
                        isChecked={Boolean(foundModule)}
                        setChecked={(isChecked: boolean) =>
                          handleModuleChange(isChecked, item.value as string)
                        }
                        showError={showError}
                        validations={RoleFormValidationSet.modules}
                      />
                    </div>
                    {permissionOptions.map((permission, pIndex) => (
                      <div className="col-2" key={pIndex}>
                        <StandardCheckbox
                          isChecked={
                            foundModule?.permissions.includes(
                              permission.value as
                                | 'create'
                                | 'view'
                                | 'edit'
                                | 'delete'
                            ) || false
                          }
                          setChecked={(isChecked: boolean) =>
                            handlePermissionChange(
                              isChecked,
                              permission.value as
                                | 'create'
                                | 'view'
                                | 'edit'
                                | 'delete',
                              item.value as string
                            )
                          }
                          label=""
                        />
                      </div>
                    ))}
                  </div>
                );
              })}
            </ConditionalRender>
          </Card.Body>
        </Form>
        {renderDeletePopup()}
      </ConditionalRender>
    </Card>
  );
};
